import { useEffect, useRef, useState, useCallback } from "react";

/**
 * useAutoMixSweep — one-tap deck-to-deck transition for the Radio DJ studio.
 *
 * Animates the FX filter from 0 → 1 → 0 while crossfading between deck A
 * and deck B over ~2.4s (or longer if `smartMix` is enabled, in which
 * case the duration is rounded to the nearest multiple of 8 beats of the
 * dominant deck's BPM, clamped 1.8–3.6s).
 *
 * Returned API:
 *   - sweeping          : boolean — true while a sweep is in flight
 *   - sweepFromRef      : RefObject — "A" | "B" after a completion, drives
 *                         SetList-Lock auto-advance on the now-silent deck
 *   - sweepCompletedAt  : number — Date.now() of the last completion;
 *                         change-detection trigger for downstream effects
 *   - sweepToNext()     : kicks off (or cancels, if already in flight) a sweep
 *   - cancelSweep()     : aborts an in-flight sweep
 *
 * Why a hook? `RadioHostStudio.jsx` was 1.2K+ lines of UI + audio routing;
 * isolating the transition state machine here keeps the page lean and
 * makes the sweep logic independently testable.
 */
export default function useAutoMixSweep({
  xfader,
  setXfader,
  deckA,
  deckB,
  playA,
  playB,
  setPlayA,
  setPlayB,
  setFxType,
  setFxAmount,
  smartMix,
}) {
  const sweepRef = useRef({ raf: null, cancelled: false });
  const [sweeping, setSweeping] = useState(false);
  const sweepFromRef = useRef(null);
  const [sweepCompletedAt, setSweepCompletedAt] = useState(0);

  const cancelSweep = useCallback(() => {
    if (sweepRef.current.raf) cancelAnimationFrame(sweepRef.current.raf);
    sweepRef.current.cancelled = true;
    setSweeping(false);
  }, []);

  const sweepToNext = useCallback(() => {
    if (sweeping) return cancelSweep();
    const fromX = xfader;
    const toX = fromX < 0.5 ? 1 : 0;
    // Make sure the incoming deck is actually playing — otherwise the
    // transition would dump the listener into silence.
    if (toX === 1 && deckB && !playB) setPlayB(true);
    if (toX === 0 && deckA && !playA) setPlayA(true);
    setFxType("filter");
    sweepRef.current.cancelled = false;
    setSweeping(true);
    const t0 = performance.now();
    // Smart-mix beat-align: round sweep duration to the nearest multiple
    // of 8 beats of the currently dominant deck's BPM. Default 2400ms
    // becomes ~2400 + drift to land on a bar. We clamp 1800..3600 to keep
    // transitions snappy.
    const baseDur = 2400;
    let dur = baseDur;
    if (smartMix) {
      const dominant = fromX < 0.5 ? deckA : deckB;
      const bpm = dominant?.bpm;
      if (bpm && bpm > 40) {
        const beatMs = 60000 / bpm;
        const eightBeats = beatMs * 8;
        const rounded = Math.round(baseDur / eightBeats) * eightBeats || eightBeats;
        dur = Math.max(1800, Math.min(3600, rounded));
      }
    }
    const tick = (now) => {
      if (sweepRef.current.cancelled) return;
      const t = Math.min(1, (now - t0) / dur);
      // Filter sweep: peak at t=0.55 (just before the audible swap), drop
      // back to 0 by t=1 so the new track starts clean.
      const sweepCurve = t < 0.55
        ? (t / 0.55)
        : Math.max(0, 1 - (t - 0.55) / 0.45);
      setFxAmount(sweepCurve);
      // Crossfader: equal-power-ish ease so the swap lands at t≈0.65
      const xT = t < 0.65 ? (t / 0.65) : 1;
      const eased = xT * xT * (3 - 2 * xT);
      setXfader(fromX + (toX - fromX) * eased);
      if (t >= 1) {
        setFxAmount(0);
        setFxType("off");
        setSweeping(false);
        // Remember which side we swept FROM so SetList Lock can load the
        // next queued track onto it. fromX < 0.5 ⇒ A was dominant ⇒ sweep
        // OUT of A; fromX >= 0.5 ⇒ swept OUT of B.
        sweepFromRef.current = fromX < 0.5 ? "A" : "B";
        setSweepCompletedAt(Date.now());
        return;
      }
      sweepRef.current.raf = requestAnimationFrame(tick);
    };
    sweepRef.current.raf = requestAnimationFrame(tick);
  }, [
    sweeping, cancelSweep, xfader, deckA, deckB, playA, playB,
    setPlayA, setPlayB, setFxType, setFxAmount, setXfader, smartMix,
  ]);

  // Cancel any in-flight sweep on unmount so a navigation-away mid-sweep
  // doesn't leak a requestAnimationFrame loop.
  useEffect(() => () => cancelSweep(), [cancelSweep]);

  return { sweeping, sweepFromRef, sweepCompletedAt, sweepToNext, cancelSweep };
}
